<template>
  <transition name="modal">
    <div v-if="show" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5" @click.self="$emit('close')">
      <div class="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-xl" @click.stop>
        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h3 class="text-base font-semibold text-gray-900">Modifier les informations</h3>
          <button class="w-8 h-8 flex items-center justify-center text-gray-300 hover:text-gray-500 border border-gray-200 rounded-lg transition-colors"
            @click="$emit('close')">
            <i class="fas fa-times text-xs"></i>
          </button>
        </div>

        <!-- Onglets -->
        <div class="flex gap-1 px-6 pt-4 pb-0 flex-shrink-0">
          <button v-for="tab in [
            { key: 'general', icon: 'fa-building', label: 'Entreprise' },
            { key: 'contact', icon: 'fa-comment', label: 'Contact' },
            { key: 'legal', icon: 'fa-file', label: 'Légal' },
            { key: 'config', icon: 'fa-cash-register', label: 'Caisse' }
          ]" :key="tab.key"
            :class="[
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors',
              activeTab === tab.key
                ? 'bg-primary text-white'
                : 'text-gray-500 hover:bg-gray-50'
            ]"
            @click="activeTab = tab.key">
            <i :class="['fas', tab.icon, 'text-[10px]']"></i>
            {{ tab.label }}
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto px-6 py-5">
          <form @submit.prevent="submitForm">
            <!-- Entreprise -->
            <div v-show="activeTab === 'general'" class="space-y-4">
              <div>
                <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Nom de l'entreprise <span class="text-red-500">*</span></p>
                <input type="text" v-model="form.name" required placeholder="Nom de votre entreprise"
                  :class="['w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors', errors.name ? 'border-red-300' : 'border-gray-200']" />
                <span v-if="errors.name" class="text-xs text-red-500 mt-1 block">{{ errors.name[0] }}</span>
              </div>
              <div>
                <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Type d'activité</p>
                <input type="text" v-model="form.business_type" placeholder="Ex: Commerce de détail, Services..."
                  :class="['w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors', errors.business_type ? 'border-red-300' : 'border-gray-200']" />
                <span v-if="errors.business_type" class="text-xs text-red-500 mt-1 block">{{ errors.business_type[0] }}</span>
              </div>
              <div>
                <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Description</p>
                <textarea v-model="form.description" placeholder="Décrivez votre entreprise..." rows="3"
                  :class="['w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none', errors.description ? 'border-red-300' : 'border-gray-200']"></textarea>
                <span v-if="errors.description" class="text-xs text-red-500 mt-1 block">{{ errors.description[0] }}</span>
              </div>
            </div>

            <!-- Contact -->
            <div v-show="activeTab === 'contact'" class="space-y-4">
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Email</p>
                  <input type="email" v-model="form.email" placeholder="contact@entreprise.com"
                    :class="['w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors', errors.email ? 'border-red-300' : 'border-gray-200']" />
                  <span v-if="errors.email" class="text-xs text-red-500 mt-1 block">{{ errors.email[0] }}</span>
                </div>
                <div>
                  <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Téléphone</p>
                  <input type="tel" v-model="form.phone" placeholder="+221 77 123 45 67"
                    :class="['w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors', errors.phone ? 'border-red-300' : 'border-gray-200']" />
                  <span v-if="errors.phone" class="text-xs text-red-500 mt-1 block">{{ errors.phone[0] }}</span>
                </div>
              </div>
              <div>
                <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Site web</p>
                <input type="url" v-model="form.website" placeholder="https://www.entreprise.com"
                  :class="['w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors', errors.website ? 'border-red-300' : 'border-gray-200']" />
                <span v-if="errors.website" class="text-xs text-red-500 mt-1 block">{{ errors.website[0] }}</span>
              </div>
              <div class="flex items-center gap-3 py-1">
                <div class="h-px flex-1 bg-gray-100"></div>
                <span class="text-[11px] text-gray-300 uppercase tracking-wider">Adresse</span>
                <div class="h-px flex-1 bg-gray-100"></div>
              </div>
              <div>
                <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Adresse</p>
                <input type="text" v-model="form.address" placeholder="Rue, numéro, quartier..."
                  :class="['w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors', errors.address ? 'border-red-300' : 'border-gray-200']" />
                <span v-if="errors.address" class="text-xs text-red-500 mt-1 block">{{ errors.address[0] }}</span>
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Ville</p>
                  <input type="text" v-model="form.city" placeholder="Dakar"
                    :class="['w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors', errors.city ? 'border-red-300' : 'border-gray-200']" />
                  <span v-if="errors.city" class="text-xs text-red-500 mt-1 block">{{ errors.city[0] }}</span>
                </div>
                <div>
                  <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Pays</p>
                  <input type="text" v-model="form.country" placeholder="Sénégal"
                    :class="['w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors', errors.country ? 'border-red-300' : 'border-gray-200']" />
                  <span v-if="errors.country" class="text-xs text-red-500 mt-1 block">{{ errors.country[0] }}</span>
                </div>
              </div>
            </div>

            <!-- Légal -->
            <div v-show="activeTab === 'legal'" class="space-y-4">
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">NINEA</p>
                  <input type="text" v-model="form.ninea" placeholder="Numéro NINEA"
                    :class="['w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors', errors.ninea ? 'border-red-300' : 'border-gray-200']" />
                  <span v-if="errors.ninea" class="text-xs text-red-500 mt-1 block">{{ errors.ninea[0] }}</span>
                </div>
                <div>
                  <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">RCCM</p>
                  <input type="text" v-model="form.rccm" placeholder="Registre du commerce"
                    :class="['w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors', errors.rccm ? 'border-red-300' : 'border-gray-200']" />
                  <span v-if="errors.rccm" class="text-xs text-red-500 mt-1 block">{{ errors.rccm[0] }}</span>
                </div>
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Numéro fiscal</p>
                  <input type="text" v-model="form.tax_id" placeholder="Numéro d'identification fiscale"
                    :class="['w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors', errors.tax_id ? 'border-red-300' : 'border-gray-200']" />
                  <span v-if="errors.tax_id" class="text-xs text-red-500 mt-1 block">{{ errors.tax_id[0] }}</span>
                </div>
                <div>
                  <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Capital</p>
                  <input type="number" v-model.number="form.capital" placeholder="Capital social" min="0"
                    :class="['w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors', errors.capital ? 'border-red-300' : 'border-gray-200']" />
                  <span v-if="errors.capital" class="text-xs text-red-500 mt-1 block">{{ errors.capital[0] }}</span>
                </div>
              </div>
            </div>

            <!-- Caisse -->
            <div v-show="activeTab === 'config'" class="space-y-4">
              <div>
                <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Devise</p>
                <select v-model="form.currency"
                  :class="['w-full px-3 py-2.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors', errors.currency ? 'border-red-300' : 'border-gray-200']">
                  <option value="XOF">XOF - Franc CFA (FCFA)</option>
                  <option value="EUR">EUR - Euro (€)</option>
                  <option value="USD">USD - Dollar ($)</option>
                  <option value="GBP">GBP - Livre Sterling (£)</option>
                </select>
                <span v-if="errors.currency" class="text-xs text-red-500 mt-1 block">{{ errors.currency[0] }}</span>
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Solde de départ caisse</p>
                  <input type="number" v-model.number="form.cash_register_start_balance" placeholder="0" min="0"
                    :class="['w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors', errors.cash_register_start_balance ? 'border-red-300' : 'border-gray-200']" />
                  <span v-if="errors.cash_register_start_balance" class="text-xs text-red-500 mt-1 block">{{ errors.cash_register_start_balance[0] }}</span>
                </div>
                <div>
                  <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Nombre max de caisses</p>
                  <input type="number" v-model.number="form.max_cash_registers" placeholder="Illimité si vide" min="1"
                    :class="['w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors', errors.max_cash_registers ? 'border-red-300' : 'border-gray-200']" />
                  <p class="text-[11px] text-gray-300 mt-1">Laisser vide pour illimité</p>
                  <span v-if="errors.max_cash_registers" class="text-xs text-red-500 mt-1 block">{{ errors.max_cash_registers[0] }}</span>
                </div>
              </div>
            </div>
          </form>
        </div>

        <!-- Footer -->
        <div class="flex gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button class="flex-1 px-3 py-2 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            @click="$emit('close')">
            Annuler
          </button>
          <button
            class="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            @click="submitForm" :disabled="isSubmitting">
            <i v-if="isSubmitting" class="fas fa-spinner fa-spin text-[10px]"></i>
            <span>{{ isSubmitting ? 'Enregistrement...' : 'Enregistrer' }}</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { defineComponent, ref, reactive, watch } from 'vue';
import companyService from '@/services/companyService';

export default defineComponent({
  name: 'EditCompanyModal',

  props: {
    show: {
      type: Boolean,
      default: false
    },
    company: {
      type: Object,
      default: null
    }
  },

  emits: ['close', 'company-updated'],

  setup(props, { emit }) {
    const activeTab = ref('general');
    const isSubmitting = ref(false);
    const errors = reactive({});
    const logoInput = ref(null);
    const logoPreview = ref(null);
    const logoFile = ref(null);
    const removeExistingLogo = ref(false);

    const form = reactive({
      name: '',
      business_type: '',
      description: '',
      email: '',
      phone: '',
      website: '',
      address: '',
      city: '',
      country: '',
      ninea: '',
      rccm: '',
      tax_id: '',
      capital: null,
      currency: 'XOF',
      cash_register_start_balance: null,
      max_cash_registers: null
    });

    watch(() => props.show, (newVal) => {
      if (newVal && props.company) {
        populateForm();
      }
    });

    watch(() => props.company, (newVal) => {
      if (props.show && newVal) {
        populateForm();
      }
    });

    const populateForm = () => {
      const c = props.company;
      if (!c) return;

      form.name = c.name || '';
      form.business_type = c.business_type || '';
      form.description = c.description || '';
      form.email = c.email || '';
      form.phone = c.phone || '';
      form.website = c.website || '';
      form.address = c.address || '';
      form.city = c.city || '';
      form.country = c.country || '';
      form.ninea = c.ninea || '';
      form.rccm = c.rccm || '';
      form.tax_id = c.tax_id || '';
      form.capital = c.capital || null;
      form.currency = c.currency || 'XOF';
      form.cash_register_start_balance = c.cash_register_start_balance ?? null;
      form.max_cash_registers = c.max_cash_registers ?? null;

      logoPreview.value = c.logo || null;
      logoFile.value = null;
      removeExistingLogo.value = false;

      Object.keys(errors).forEach(key => delete errors[key]);
      activeTab.value = 'general';
    };

    const triggerLogoInput = () => {
      logoInput.value?.click();
    };

    const onLogoSelected = (event) => {
      const file = event.target.files?.[0];
      if (file) {
        handleLogoFile(file);
      }
    };

    const onDragOver = (event) => {
      event.currentTarget.classList.add('drag-over');
    };

    const onDragLeave = (event) => {
      event.currentTarget.classList.remove('drag-over');
    };

    const onLogoDrop = (event) => {
      event.currentTarget.classList.remove('drag-over');
      const file = event.dataTransfer.files?.[0];
      if (file && file.type.startsWith('image/')) {
        handleLogoFile(file);
      }
    };

    const handleLogoFile = (file) => {
      if (file.size > 2 * 1024 * 1024) {
        errors.logo = ['Le fichier ne doit pas dépasser 2MB'];
        return;
      }

      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
        errors.logo = ['Le fichier doit être une image JPG ou PNG'];
        return;
      }

      delete errors.logo;
      logoFile.value = file;
      removeExistingLogo.value = false;

      const reader = new FileReader();
      reader.onload = (e) => {
        logoPreview.value = e.target.result;
      };
      reader.readAsDataURL(file);
    };

    const removeLogo = () => {
      logoFile.value = null;
      logoPreview.value = null;
      removeExistingLogo.value = true;
      if (logoInput.value) {
        logoInput.value.value = '';
      }
    };

    const submitForm = async () => {
      if (isSubmitting.value) return;

      if (!form.name.trim()) {
        errors.name = ['Le nom de l\'entreprise est requis'];
        activeTab.value = 'general';
        return;
      }

      isSubmitting.value = true;
      Object.keys(errors).forEach(key => delete errors[key]);

      try {
        const companyData = {
          name: form.name,
          business_type: form.business_type || null,
          description: form.description || null,
          email: form.email || null,
          phone: form.phone || null,
          website: form.website || null,
          address: form.address || null,
          city: form.city || null,
          country: form.country || null,
          ninea: form.ninea || null,
          rccm: form.rccm || null,
          tax_id: form.tax_id || null,
          capital: form.capital || null,
          currency: form.currency,
          cash_register_start_balance: form.cash_register_start_balance ?? null,
          max_cash_registers: form.max_cash_registers || null
        };

        if (logoFile.value) {
          companyData.logo = logoFile.value;
        }

        const response = await companyService.updateCompany(props.company.id, companyData);

        if (removeExistingLogo.value && !logoFile.value && props.company.logo) {
          try {
            await companyService.deleteLogo(props.company.id);
          } catch (e) {
            console.warn('Erreur lors de la suppression du logo:', e);
          }
        }

        const updatedCompany = response.data.company || response.data;
        localStorage.setItem('company', JSON.stringify(updatedCompany));

        emit('company-updated', updatedCompany);
        emit('close');
      } catch (error) {
        console.error('Erreur lors de la mise à jour:', error);

        if (error.response && error.response.status === 422) {
          const validationErrors = error.response.data.errors || {};
          Object.keys(validationErrors).forEach(key => {
            errors[key] = validationErrors[key];
          });

          const errorKeys = Object.keys(validationErrors);
          if (errorKeys.some(k => ['name', 'business_type', 'description', 'logo'].includes(k))) {
            activeTab.value = 'general';
          } else if (errorKeys.some(k => ['email', 'phone', 'website', 'address', 'city', 'country'].includes(k))) {
            activeTab.value = 'contact';
          } else if (errorKeys.some(k => ['ninea', 'rccm', 'tax_id', 'capital'].includes(k))) {
            activeTab.value = 'legal';
          } else if (errorKeys.some(k => ['currency', 'cash_register_start_balance', 'max_cash_registers'].includes(k))) {
            activeTab.value = 'config';
          }
        } else {
          alert('Une erreur est survenue lors de la mise à jour');
        }
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      activeTab,
      form,
      errors,
      isSubmitting,
      logoInput,
      logoPreview,
      triggerLogoInput,
      onLogoSelected,
      onDragOver,
      onDragLeave,
      onLogoDrop,
      removeLogo,
      submitForm
    };
  }
});
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
